import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import messageService from '../services/messageService';

/**
 * MessageContext — polls /api/messages/unread-count every 30s so the Navbar
 * messages icon can render a live unread badge. Mirrors NotificationContext's
 * pattern. Lightweight (single int + boolean), so it's safe to wrap the whole
 * app in.
 */
const MessageContext = createContext(null);
const POLL_INTERVAL = 30000;

export function MessageProvider({ children }) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const pollRef = useRef(null);

  const fetchUnread = useCallback(async () => {
    if (!isAuthenticated) return;
    const r = await messageService.getUnreadCount();
    if (r?.success && r?.data) {
      // Backend returns { unread: N } — see MessagesController.GetUnreadCount.
      const n = typeof r.data.unread === 'number'
        ? r.data.unread
        : (typeof r.data.unreadCount === 'number' ? r.data.unreadCount : 0);
      setUnreadCount(n);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      fetchUnread();
      pollRef.current = setInterval(fetchUnread, POLL_INTERVAL);
    } else {
      setUnreadCount(0);
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [isAuthenticated, authLoading, fetchUnread]);

  return (
    <MessageContext.Provider value={{ unreadCount, fetchUnread }}>
      {children}
    </MessageContext.Provider>
  );
}

export function useMessages() {
  const ctx = useContext(MessageContext);
  if (!ctx) throw new Error('useMessages must be used within a MessageProvider');
  return ctx;
}

export default MessageContext;
